export const PALETTE = [
  "#017bfe",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#64748b",
];

/* ------------------------------ periods ------------------------------ */
export const REPORT_PERIODS = {
  TODAY: "TODAY",
  WTD: "WTD",
  MTD: "MTD",
  LM: "LM",
  L30: "L30",
  L90: "L90",
  QTD: "QTD",
  YTD: "YTD",
  CUSTOM: "CUSTOM",
};

export const PERIOD_OPTIONS = [
  [REPORT_PERIODS.TODAY, "Today"],
  [REPORT_PERIODS.WTD, "This week"],
  [REPORT_PERIODS.MTD, "This month"],
  [REPORT_PERIODS.LM, "Last month"],
  [REPORT_PERIODS.L30, "Last 30 days"],
  [REPORT_PERIODS.L90, "Last 90 days"],
  [REPORT_PERIODS.QTD, "This quarter"],
  [REPORT_PERIODS.YTD, "This year"],
  [REPORT_PERIODS.CUSTOM, "Custom"],
];

const pad = (n) => String(n).padStart(2, "0");
export const toDateString = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseDate = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const getPeriodDates = (period, today = new Date()) => {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  let end = new Date(y, m, d);
  let start = new Date(end);
  switch (period) {
    case REPORT_PERIODS.WTD:
      start.setDate(d - ((end.getDay() + 6) % 7));
      break;
    case REPORT_PERIODS.MTD:
      start = new Date(y, m, 1);
      break;
    case REPORT_PERIODS.LM:
      start = new Date(y, m - 1, 1);
      end = new Date(y, m, 0);
      break;
    case REPORT_PERIODS.L30:
      start.setDate(d - 29);
      break;
    case REPORT_PERIODS.L90:
      start.setDate(d - 89);
      break;
    case REPORT_PERIODS.QTD:
      start = new Date(y, Math.floor(m / 3) * 3, 1);
      break;
    case REPORT_PERIODS.YTD:
      start = new Date(y, 0, 1);
      break;
    default:
      break;
  }
  return { startDate: toDateString(start), endDate: toDateString(end) };
};

/** Same-length range immediately before the given one. */
export const getPreviousRange = ({ startDate, endDate }) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const days = Math.round((end - start) / 864e5) + 1;
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  return { startDate: toDateString(prevStart), endDate: toDateString(prevEnd) };
};

/* ----------------------------- formatting ----------------------------- */
export const formatNumber = (v, max = 2) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: max }).format(
    Number(v) || 0,
  );

export const formatCompact = (v) =>
  new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(v) || 0);

export const formatCurrency = (v, currency = "INR", compact = true) => {
  try {
    return new Intl.NumberFormat(currency === "INR" ? "en-IN" : undefined, {
      style: "currency",
      currency,
      notation: compact ? "compact" : "standard",
      maximumFractionDigits: compact ? 1 : 0,
    }).format(Number(v) || 0);
  } catch {
    return `${currency} ${formatNumber(v)}`;
  }
};

export const formatValue = (v, unit = "COUNT", currency = "INR") => {
  const n = Number(v) || 0;
  switch (String(unit).toUpperCase()) {
    case "CURRENCY":
      return formatCurrency(n, currency);
    case "PERCENT":
      return `${formatNumber(n, 1)}%`;
    case "DAYS":
      return `${formatNumber(n, 1)}d`;
    case "HOURS":
      return `${formatNumber(n, 1)}h`;
    case "DECIMAL":
      return formatNumber(n, 2);
    default:
      return Math.abs(n) >= 100000 ? formatCompact(n) : formatNumber(n);
  }
};

export const formatDate = (value, includeTime = false) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" } : {}),
  }).format(date);
};

export const formatBucket = (bucket, granularity) => {
  if (!bucket) return "";
  const d = parseDate(bucket);
  if (Number.isNaN(d.getTime())) return String(bucket);
  if (String(granularity).toUpperCase() === "MONTH")
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      year: "2-digit",
    }).format(d);
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
  }).format(d);
};

export const humanize = (key) =>
  String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

export const getStatusClass = (value) =>
  `rp-status rp-status-${String(value || "unknown")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

export const omit = (obj, keys) => {
  const copy = { ...obj };
  keys.forEach((k) => delete copy[k]);
  return copy;
};

/* ------------------- tolerant shape helpers (distributions) ------------------- */
const NAME_KEYS = [
  "label",
  "name",
  "ownerName",
  "employeeName",
  "assignedEmployeeName",
  "clientName",
  "stageName",
  "source",
  "industry",
  "reason",
  "category",
  "transactionType",
  "warehouseName",
  "stockUpdatedByEmployeeName",
  "stockName",
  "productName",
  "warehouseStatus",
  "status",
  "key",
];

export const pickName = (item) => {
  if (item === null || item === undefined) return "Unknown";
  if (typeof item !== "object") return String(item);
  for (const k of NAME_KEYS)
    if (item[k] !== undefined && item[k] !== null && item[k] !== "")
      return String(item[k]);
  const first = Object.values(item).find((v) => typeof v === "string");
  return first || "Unknown";
};

export const pickNumber = (item, keys = ["count", "value"]) => {
  if (!item || typeof item !== "object") return Number(item) || 0;
  for (const k of keys) {
    const v = item[k];
    if (v !== undefined && v !== null && v !== "" && Number.isFinite(Number(v)))
      return Number(v);
  }
  const fallback = Object.entries(item).find(
    ([k, v]) =>
      !/id$/i.test(k) &&
      v !== "" &&
      v !== null &&
      typeof v !== "object" &&
      Number.isFinite(Number(v)),
  );
  return fallback ? Number(fallback[1]) : 0;
};

export const toDist = (items, valueKeys) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    name: pickName(item),
    key: item?.key ?? pickName(item),
    value: pickNumber(item, valueKeys),
    raw: item,
  }));

/* --------------------------------- KPIs --------------------------------- */
const calcChange = (value, previous) =>
  previous === undefined || previous === null || Number(previous) === 0
    ? null
    : ((value - Number(previous)) / Math.abs(Number(previous))) * 100;

const finishKpi = (k) => {
  const value = Number(k.value) || 0;
  const previousValue =
    k.previousValue === undefined || k.previousValue === null
      ? null
      : Number(k.previousValue);
  const changePct = k.changePct ?? calcChange(value, previousValue);
  const trend =
    k.trend ||
    (changePct === null
      ? "FLAT"
      : changePct > 0
        ? "UP"
        : changePct < 0
          ? "DOWN"
          : "FLAT");
  return {
    unit: "COUNT",
    goodDirection: "UP",
    ...k,
    value,
    previousValue,
    changePct,
    trend,
  };
};

/** Accepts an array of KPI objects (overview) or a flat map (other reports) with `defs`. */
export const buildKpis = (raw, defs = []) => {
  if (Array.isArray(raw)) return raw.map(finishKpi);
  if (!raw || typeof raw !== "object") return [];
  const known = new Set(defs.flatMap((d) => [d.key, d.prev]));
  const fromDefs = defs
    .filter((d) => raw[d.key] !== undefined && raw[d.key] !== null)
    .map((d) => {
      const r = raw[d.key];
      const base =
        r && typeof r === "object"
          ? r
          : { value: r, previousValue: d.prev ? raw[d.prev] : undefined };
      return finishKpi({
        key: d.key,
        label: d.label,
        desc: d.desc,
        unit: d.unit,
        goodDirection: d.good,
        icon: d.icon,
        ...base,
      });
    });
  const extra = Object.keys(raw)
    .filter(
      (k) =>
        !known.has(k) &&
        !/^previous/i.test(k) &&
        raw[k] !== null &&
        typeof raw[k] !== "object" &&
        Number.isFinite(Number(raw[k])),
    )
    .map((k) => finishKpi({ key: k, label: humanize(k), value: raw[k] }));
  return fromDefs.length ? fromDefs : extra;
};

export const applyPrevious = (kpis, previousRaw) =>
  kpis.map((k) =>
    finishKpi({
      ...k,
      previousValue: previousRaw?.[k.key],
      changePct: undefined,
      trend: undefined,
    }),
  );

/* ------------------------- filter option discovery ------------------------- */
export const collectOptions = (payload, prev = {}) => {
  const sets = {
    sources: new Map((prev.sources || []).map((o) => [o.value, o])),
    industries: new Map((prev.industries || []).map((o) => [o.value, o])),
    owners: new Map((prev.owners || []).map((o) => [o.value, o])),
    stages: new Map((prev.stages || []).map((o) => [o.value, o])),
    statuses: new Map((prev.statuses || []).map((o) => [o.value, o])),
    priorities: new Map((prev.priorities || []).map((o) => [o.value, o])),
    types: new Map((prev.types || []).map((o) => [o.value, o])),
  };
  const add = (set, value, label) => {
    if (value === undefined || value === null || value === "") return;
    const v = String(value);
    if (!sets[set].has(v))
      sets[set].set(v, { value: v, label: label ? String(label) : v });
  };
  const distSets = {
    bySource: "sources",
    leadSources: "sources",
    byIndustry: "industries",
    byStatus: "statuses",
    enquiryStatus: "statuses",
    byPriority: "priorities",
    byType: "types",
  };
  const scan = (node, name, depth) => {
    if (!node || depth > 7) return;
    if (Array.isArray(node)) {
      if (distSets[name])
        node.forEach((i) => add(distSets[name], i?.key ?? i?.label));
      node.forEach((i) => scan(i, name, depth + 1));
      return;
    }
    if (typeof node !== "object") return;
    if (typeof node.source === "string") add("sources", node.source);
    if (typeof node.industry === "string") add("industries", node.industry);
    if (node.assignedEmployeeId != null && node.assignedEmployeeName)
      add("owners", node.assignedEmployeeId, node.assignedEmployeeName);
    if (node.employeeId != null && node.employeeName)
      add("owners", node.employeeId, node.employeeName);
    if (node.stageId != null && node.stageName)
      add("stages", node.stageId, node.stageName);
    if (
      node.leadId != null &&
      typeof node.status === "string" &&
      !node.enquiryCode
    )
      add("statuses", node.status);
    if (node.enquiryCode) {
      add("statuses", node.status);
      add("priorities", node.priority);
      add("types", node.type);
    }
    Object.entries(node).forEach(([k, v]) => {
      if (v && typeof v === "object") scan(v, k, depth + 1);
    });
  };
  scan(payload, "", 0);
  const out = {};
  Object.entries(sets).forEach(([k, m]) => {
    out[k] = [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  });
  return out;
};

/* ---------------------------------- CSV ---------------------------------- */
export const downloadCsv = (filename, columns, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = columns.map((c) => esc(c.header)).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => esc(c.exportValue ? c.exportValue(r) : r[c.key]))
        .join(","),
    )
    .join("\n");
  const blob = new Blob([`\uFEFF${head}\n${body}`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ------------------------ operations (client-side) ------------------------ */
// The operation summary endpoint still returns the nested structure, so it is aggregated here.
const DONE = /COMPLET|DONE|FINISH|CLOSED/;
const PROG = /PROGRESS|START|RUNNING|ONGOING|ACTIVE/;
const PEND = /PENDING|ASSIGN|NEW|WAIT/;
const HOLD = /HOLD|PAUSE/;
const up = (v) => String(v || "").toUpperCase();

export const groupCount = (rows, fn, top) => {
  const m = new Map();
  rows.forEach((r) => {
    const k = fn(r) || "Unknown";
    m.set(k, (m.get(k) || 0) + 1);
  });
  const out = [...m]
    .map(([name, value]) => ({ name, key: name, value }))
    .sort((a, b) => b.value - a.value);
  return top ? out.slice(0, top) : out;
};

export const flattenOperations = (data) => {
  const rows = [];
  (data?.categories || []).forEach((c) =>
    (c.products || []).forEach((p) =>
      (p.operationStages || []).forEach((s) =>
        (s.employees || []).forEach((e) =>
          (e.operationHistories || []).forEach((h) => {
            const start = h.operationsStartedAt
              ? new Date(h.operationsStartedAt)
              : null;
            const end = h.operationsEndedAt
              ? new Date(h.operationsEndedAt)
              : null;
            const tasks = h.checklists || [];
            rows.push({
              id: h.operationStageHistoryId,
              categoryName: c.categoryName,
              productName: h.productName || p.productName,
              stageName: s.stageName,
              sequence: s.sequenceNumber,
              employeeName: e.employeeName,
              status: h.status,
              qcStatus: h.qcStatus,
              returned: !!h.returned,
              startedAt: h.operationsStartedAt,
              endedAt: h.operationsEndedAt,
              durationHours:
                start && end && end >= start ? (end - start) / 36e5 : null,
              tasksTotal: tasks.length,
              tasksDone: tasks.filter((t) => t.completed).length,
            });
          }),
        ),
      ),
    ),
  );
  return rows;
};

const avg = (list) =>
  list.length ? list.reduce((s, n) => s + n, 0) / list.length : 0;

export const summarizeOperations = (data, dates) => {
  const rows = flattenOperations(data);
  const completed = rows.filter((r) => DONE.test(up(r.status))).length;
  const inProgress = rows.filter((r) => PROG.test(up(r.status))).length;
  const pending = rows.filter((r) => PEND.test(up(r.status))).length;
  const onHold = rows.filter((r) => HOLD.test(up(r.status))).length;
  const pass = rows.filter((r) => /PASS|APPROV/.test(up(r.qcStatus))).length;
  const fail = rows.filter((r) => /FAIL|REJECT/.test(up(r.qcStatus))).length;
  const durations = rows.map((r) => r.durationHours).filter((n) => n !== null);
  const tasksTotal = rows.reduce((s, r) => s + r.tasksTotal, 0);
  const tasksDone = rows.reduce((s, r) => s + r.tasksDone, 0);
  const total = Number(data?.totalOperations) || rows.length;

  const stageMap = new Map();
  rows.forEach((r) => {
    const s = stageMap.get(r.stageName) || {
      name: r.stageName || "Unknown",
      sequence: r.sequence ?? 999,
      total: 0,
      done: 0,
      pending: 0,
      dur: [],
    };
    s.total += 1;
    if (DONE.test(up(r.status))) s.done += 1;
    if (PEND.test(up(r.status))) s.pending += 1;
    if (r.durationHours !== null) s.dur.push(r.durationHours);
    stageMap.set(r.stageName, s);
  });
  const byStage = [...stageMap.values()]
    .sort((a, b) => a.sequence - b.sequence)
    .map((s) => ({ ...s, avgDuration: avg(s.dur) }));
  const slowest = [...byStage]
    .filter((s) => s.dur.length)
    .sort((a, b) => b.avgDuration - a.avgDuration)[0];
  const mostPending = [...byStage].sort((a, b) => b.pending - a.pending)[0];

  const empMap = new Map();
  rows.forEach((r) => {
    const e = empMap.get(r.employeeName) || {
      name: r.employeeName || "Unknown",
      total: 0,
      done: 0,
      pass: 0,
      fail: 0,
      dur: [],
    };
    e.total += 1;
    if (DONE.test(up(r.status))) e.done += 1;
    if (/PASS|APPROV/.test(up(r.qcStatus))) e.pass += 1;
    if (/FAIL|REJECT/.test(up(r.qcStatus))) e.fail += 1;
    if (r.durationHours !== null) e.dur.push(r.durationHours);
    empMap.set(r.employeeName, e);
  });
  const byEmployee = [...empMap.values()]
    .map((e) => ({
      ...e,
      qcPassRate: e.pass + e.fail ? (e.pass / (e.pass + e.fail)) * 100 : null,
      avgDuration: avg(e.dur),
    }))
    .sort((a, b) => b.total - a.total);

  const bucketDefs = [
    ["< 1h", 0, 1],
    ["1–4h", 1, 4],
    ["4–8h", 4, 8],
    ["8–24h", 8, 24],
    ["> 24h", 24, Infinity],
  ];
  const durationBuckets = bucketDefs.map(([name, lo, hi]) => ({
    name,
    key: name,
    value: durations.filter((d) => d >= lo && d < hi).length,
  }));

  const day = (v) => (v ? toDateString(new Date(v)) : null);
  const trendMap = new Map();
  const bump = (k, f) => {
    if (!k) return;
    const t = trendMap.get(k) || { bucket: k, started: 0, completed: 0 };
    t[f] += 1;
    trendMap.set(k, t);
  };
  rows.forEach((r) => {
    bump(day(r.startedAt), "started");
    if (DONE.test(up(r.status))) bump(day(r.endedAt), "completed");
  });
  let trend = [...trendMap.values()].sort((a, b) =>
    a.bucket.localeCompare(b.bucket),
  );
  if (dates?.startDate && dates?.endDate) {
    const s = parseDate(dates.startDate);
    const e = parseDate(dates.endDate);
    if ((e - s) / 864e5 <= 92) {
      trend = [];
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const k = toDateString(d);
        trend.push(trendMap.get(k) || { bucket: k, started: 0, completed: 0 });
      }
    }
  }

  return {
    rows,
    trend,
    byStage,
    byEmployee,
    durationBuckets,
    byStatus: groupCount(rows, (r) => r.status),
    byCategory: groupCount(rows, (r) => r.categoryName, 10),
    byProduct: groupCount(rows, (r) => r.productName, 10),
    qc: groupCount(rows, (r) => r.qcStatus || "Not inspected"),
    bottleneck: slowest?.name || mostPending?.name || "—",
    kpis: {
      totalOperations: total,
      completedOperations: completed,
      inProgressOperations: inProgress,
      pendingOperations: pending,
      onHoldOperations: onHold,
      completionRate: rows.length ? (completed / rows.length) * 100 : 0,
      qcPassRate: pass + fail ? (pass / (pass + fail)) * 100 : 0,
      qcFailed: fail,
      returnedOperations: rows.filter((r) => r.returned).length,
      avgDurationHours: avg(durations),
      checklistCompletion: tasksTotal ? (tasksDone / tasksTotal) * 100 : 0,
    },
  };
};

/* --------------------------- tolerant row helpers --------------------------- */
export const numify = (rows) =>
  (Array.isArray(rows) ? rows : []).map((r) =>
    Object.fromEntries(
      Object.entries(r || {}).map(([k, v]) => [
        k,
        typeof v === "string" &&
        v !== "" &&
        Number.isFinite(Number(v)) &&
        !/^(bucket|date|month)$/.test(k)
          ? Number(v)
          : v,
      ]),
    ),
  );

export const num = (o, k) =>
  o?.[k] === undefined || o?.[k] === null || o?.[k] === ""
    ? null
    : Number(o[k]);

/* ------------------------- plain-language wording ------------------------- */
export const describePeriod = (period, dates) =>
  ({
    TODAY: "today",
    WTD: "this week",
    MTD: "this month",
    LM: "last month",
    L30: "in the last 30 days",
    L90: "in the last 90 days",
    QTD: "this quarter",
    YTD: "this year",
  })[period] ||
  `from ${formatDate(dates.startDate)} to ${formatDate(dates.endDate)}`;

export const describeCompare = (compare) =>
  compare === "PREVIOUS_YEAR" ? "last year" : "before";
